import { Progress, Space, SpaceProps as AntdSpaceProps } from 'antd';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { Cell, Grid } from 'styled-css-grid';
import { space, SpaceProps, TextColorProps } from 'styled-system';

import { colorsV2 } from '../../../colors-v2';
import { DOLThemeType } from '../../../Common/theme';
import { DOLTypographyVariant, Typography } from '../../../DesignSystemV2/Typography';
import { DotDivider } from '../../../Divider/DotDivider';

const DEFAULT_TYPO = 'regular/14';

export interface MetadataModel {
  id: string;
  text?: React.ReactNode;
  icon?: JSX.Element;
}

export interface MetaTextProps extends TextColorProps<DOLThemeType>, SpaceProps {
  typoVariant?: DOLTypographyVariant;
  typoVariantTablet?: DOLTypographyVariant;
  typoVariantDesktop?: DOLTypographyVariant;
  children?: React.ReactNode;
  className?: string;
}

export interface MetaProps extends MetaTextProps {
  metadata?: MetadataModel[];
}

const Layout = styled(Space)`
  ${space}
  .meta-text {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
`;

const MetaText = ({
  children,
  typoVariant = DEFAULT_TYPO,
  typoVariantTablet = DEFAULT_TYPO,
  typoVariantDesktop = DEFAULT_TYPO,
  color = 'gray100',
  className,
}: MetaTextProps) => (
  <Typography.Text
    variant={typoVariant}
    tabletVariant={typoVariantTablet}
    desktopVariant={typoVariantDesktop}
    color={color}
    className={cl(className, 'meta-text')}
  >
    {children}
  </Typography.Text>
);

interface MetaLayoutProps extends AntdSpaceProps, React.HTMLAttributes<HTMLDivElement> {}

const MetaLayout = ({ children, ...rest }: MetaLayoutProps) => (
  <Layout split={<DotDivider />} size={0} wrap {...rest}>
    {children}
  </Layout>
);

// Move Progress bar out of Meta component because it too specific case and cause bug on Meta
export const MetaInProgressBar = (props: MetaProps) => {
  const { className, metadata, ...rest } = props;
  return (
    <MetaLayout className={className} split={null} style={{ width: '100%' }} size={8}>
      {metadata
        ?.filter((metaItem) => Boolean(metaItem?.text) || React.isValidElement(metaItem))
        .map((item, index) => {
          if (React.isValidElement(item)) {
            return <React.Fragment key={index}>{item}</React.Fragment>;
          }
          const { id, text, icon } = item;
          return (
            <MetaText key={id} {...rest}>
              {icon}
              {text}
            </MetaText>
          );
        })}
    </MetaLayout>
  );
};

export const InProgressBar = ({ percent, text }) => (
  <Grid columns="1fr max-content">
    <Cell middle>
      <MetaText>{text}</MetaText>
    </Cell>
    <Cell middle>
      <ProgressWrapper>
        <Progress
          trailColor={colorsV2.yellow20}
          strokeColor={colorsV2.yellow100}
          percent={percent}
          showInfo={false}
        />
      </ProgressWrapper>
    </Cell>
  </Grid>
);

const ProgressWrapper = styled.div`
  width: 70px;
  height: 18px;
  display: flex;
  align-items: center;
`;
